import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { combineLatestWith, Subject } from 'rxjs';
import { LocationListService } from '../location-list/location-list.service';
import { CCRowSetting } from '../../../models/locations-models/cc-row-setting';
import { canCreateGbpConnection } from '../location-utils';
import { RowActions } from '../location-list/state/location-list-actions';
import { CCActionBarActions, CCListRefreshActions, CCRowActions } from './state/cc-list-actions';
import { selectCCTableData } from './state/cc-list-combined-selectors';
import { LocationListLocalStoreService } from '../location-list/location-list-local-store.service';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { LocationListApiService } from '../location-list/location-list-api.service';
import { JumperComponent } from '../../global/jumper/jumper.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { DatePipe, NgClass } from '@angular/common';
import { GtagEventDirective } from '../../../core/gx-gtag/gtag-event.directive';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { CcGmbLocationGrpComponent } from './gmb-location-grp/cc-gmb-location-grp.component';
import { CcRadiusComponent } from './radius/cc-radius.component';
import { CcKeywordsBarComponent } from './keywords-chips/cc-keywords-bar.component';
import { LetDirective, PushPipe } from '@ngrx/component';
import { CellMouseOverEvent, ColDef, ColumnMovedEvent, ColumnPinnedEvent, ColumnResizedEvent, FilterChangedEvent, GridOptions, GridReadyEvent, RowDataUpdatedEvent, SelectionChangedEvent, SortChangedEvent } from 'ag-grid-community';
import { CCLocation } from '../../../models/locations-models/cc-location';
import { AgGridAngular } from 'ag-grid-angular';
import { Dictionary } from '@ngrx/entity';
import { CcListApiService } from './cc-list-api.service';
import { CcListLocalStoreService } from './cc-list-local-store.service';
import { CC_COLUMN_DEFS, CC_DEFAULT_COLUMN_DEFS, CC_GRID_OPTIONS } from './cc-list-constants';

@Component({
    selector: 'app-cc-list',
    templateUrl: './cc-list.component.html',
    styleUrls: ['./cc-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
  imports: [
    LetDirective,
    CcKeywordsBarComponent,
    CcRadiusComponent,
    CcGmbLocationGrpComponent,
    MatDivider,
    MatButton,
    GtagEventDirective,
    NgClass,
    MatIcon,
    MatTooltip,
    MatProgressSpinner,
    MatCheckbox,
    JumperComponent,
    DatePipe,
    AgGridAngular,
    PushPipe,
  ],
})
export class CCListComponent implements OnDestroy {

  viewModel$ = this.store.select(selectCCTableData)
      .pipe(
          combineLatestWith(
              this.locationListLocalStore.total$,
          ),
          map(([ccTableData, total]) => {
            const focusedLocationIndex = ccTableData.focusedLocationId ?
                this.locationListApi.getRowById(String(ccTableData.focusedLocationId))?.rowIndex :
                undefined;

            return {...ccTableData, total, focusedLocationIndex};
          })
      );

  // since we filter out the initial 'undefined' when no location is selected
  // this cannot be part of viewModel$ since it would hold off the initial emission
  // (see combinedLatestWith() spec) causing (in combination with *ngrxLet)
  // BLS-6848
  delayedLoading$ = this.localStore.delayedLoading$;

  readonly defaultColumnDefs: ColDef<CCLocation> = CC_DEFAULT_COLUMN_DEFS;
  readonly columnDefs: ColDef<CCLocation>[] = CC_COLUMN_DEFS;
  readonly gridOptions: GridOptions = CC_GRID_OPTIONS;

  private componentDestroyed = new Subject<void>();

  constructor(private store: Store,
              private locationListLocalStore: LocationListLocalStoreService,
              public locationListService: LocationListService,
              private locationListApi: LocationListApiService,
              private ccListApi: CcListApiService,
              private localStore: CcListLocalStoreService) {
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
    // update these since services are on skeleton lvl
    this.ccListApi.gridApi = null;
    this.localStore.updateGridReady(false);
  }

  refresh() {
    this.store.dispatch(CCListRefreshActions.updateCCList());
  }

  /**
   * @param args
   */
  canCreateGbpConnection(...args: Parameters<typeof canCreateGbpConnection>) {
    return canCreateGbpConnection(...args);
  }

  /**
   * Make sure another location is focused after creating a GBP connection.
   * @param e
   */
  onContinueAfterCreateChange(e: MatCheckboxChange) {
    this.store.dispatch(CCActionBarActions.updateContinueAfterCreate({
      continueAfterCreate: e.checked}));
  }

  /**
   * Make sure another location is focused after claiming a location.
   * @param e
   */
  onContinueAfterClaimChange(e: MatCheckboxChange) {
    this.store.dispatch(CCActionBarActions.updateContinueAfterClaim({
      continueAfterClaim: e.checked}))
  }

  /**
   * Change focused row in Location list and bring it into view.
   * @param focusedRowIndex 0-based
   */
  changeFocusedRow(focusedRowIndex: number) {
    const nextRow = this.locationListApi.getRowByIndex(focusedRowIndex);
    this.store.dispatch(RowActions.updateFocusedRow({
      id: Number(nextRow?.id)
    }));
    this.locationListApi.scrollRowToView(nextRow);
  }

  /**
   * Set hovered prop of given ccRowSetting to 'true' which will highlight the respective table
   * row and marker (and also increases z-index in case of a marker)
   * @param e
   * @param hoveredCcRowSetting
   * @param ccRowSettingsDictionary
   * @param focusedLocationId
   */
  onCellMouseOver(e: CellMouseOverEvent<CCLocation>,
                  hoveredCcRowSetting: CCRowSetting,
                  ccRowSettingsDictionary: Dictionary<CCRowSetting>,
                  focusedLocationId: number) {
    // seems to be some race condition issue when user hovers over a row while rows are being redrawn
    if (!ccRowSettingsDictionary[e.data.placeId]) {
      console.warn('row setting does not exist');
      return;
    }

    if (hoveredCcRowSetting && e.data.placeId !== hoveredCcRowSetting.placeId) {
      const oldHoveredCCRowSetting = { ...hoveredCcRowSetting };
      const newHoveredCCRowSetting = { ...ccRowSettingsDictionary[e.data.placeId]};
      oldHoveredCCRowSetting.hovered = false;
      newHoveredCCRowSetting.hovered = true;

      this.store.dispatch(CCRowActions.updateManyCcRowSettings({
        focusedLocationId,
        ccRowSettings: [oldHoveredCCRowSetting, newHoveredCCRowSetting]
      }));
    } else if (!hoveredCcRowSetting) {
      const newHoveredCCRowSetting = { ...ccRowSettingsDictionary[e.data.placeId]};
      newHoveredCCRowSetting.hovered = true;

      this.store.dispatch(CCRowActions.updateCcRowSetting({
        focusedLocationId,
        ccRowSetting: newHoveredCCRowSetting
      }));
    }
  }

  /**
   * @param hoveredCcRowSetting
   * @param focusedLocationId
   */
  onCellMouseOut(hoveredCcRowSetting: CCRowSetting, focusedLocationId: number) {
    this.resetHoveredRow(hoveredCcRowSetting, focusedLocationId);
  }

  /**
   * @param hoveredCcRowSetting
   * @param focusedLocationId
   */
  onGridMouseLeave(hoveredCcRowSetting: CCRowSetting, focusedLocationId: number) {
    this.resetHoveredRow(hoveredCcRowSetting, focusedLocationId);
  }

  /**
   * Somewhat safe to use the api, but does not guarantee that rows are rendered
   * @param e
   */
  onGridReady(e: GridReadyEvent<CCLocation>) {
    this.ccListApi.gridApi = e.api;
    this.localStore.updateGridReady(true);
  }

  /**
   * Rerun filters and sorting on data change.
   * @param e
   */
  onRowDataUpdated(e: RowDataUpdatedEvent) {
    e.api.onFilterChanged();
    e.api.onSortChanged();
  }

  /******************************* Sync Grid => store *********************************************/

  /**
   * Sync Grid selection state with the store.
   * @param e
   * @param ccRowSettings
   * @param focusedLocationId
   */
  onSelectionChanged(e: SelectionChangedEvent<CCLocation>,
                     ccRowSettings: Dictionary<CCRowSetting>,
                     focusedLocationId: number) {
    if (!ccRowSettings || !Object.keys(ccRowSettings).length) {
      console.warn('ccRowSettings do not exist');
      return;
    }

    const selectedRowsIds = e.api.getSelectedRows()?.map(l => l.placeId);
    const newCcRowSettings: CCRowSetting[] = []

    Object.keys(ccRowSettings).forEach(k => {
      if (ccRowSettings[k]) {
        const ccRowSettingCopy = {...ccRowSettings[k]};
        ccRowSettingCopy.selected = selectedRowsIds.includes(ccRowSettingCopy.placeId);
        newCcRowSettings.push(ccRowSettingCopy);
      }
    });

    this.store.dispatch(CCRowActions.setCcRowSettings({
      ccRowSettings: newCcRowSettings,
      focusedLocationId }));
  }

  /**
   * Sync Grid column state with the store
   * @param e
   */
  onColumnPinned(e: ColumnPinnedEvent<CCLocation>) {
    this.localStore.updateColumnState(e.api.getColumnState());
  }

  /**
   * Sync Grid column state with the store
   * @param e
   */
  onColumnMoved(e: ColumnMovedEvent<CCLocation>) {
    this.localStore.updateColumnState(e.api.getColumnState());
  }

  /**
   * Sync Grid column state with the store
   * @param e
   */
  onColumnResized(e: ColumnResizedEvent<CCLocation>) {
    this.localStore.updateColumnState(e.api.getColumnState());
  }

  /**
   * Sync Grid column state with the store
   * @param e
   */
  onSortChanged(e: SortChangedEvent<CCLocation>) {
    this.localStore.updateColumnState(e.api.getColumnState());
  }

  /**
   * Sync filter model with the store
   * @param e
   */
  onFilterChanged(e: FilterChangedEvent<CCLocation>) {
    this.localStore.updateFilterModel(e.api.getFilterModel());
  }

  /**
   * Set hovered prop of given ccRowSetting to 'false' which will remove highlight from the respective
   * table row and marker (and also decreases z-index in case of a marker)
   * @param hoveredCcRowSetting
   * @param focusedLocationId
   */
  private resetHoveredRow(hoveredCcRowSetting: CCRowSetting, focusedLocationId: number) {
    if (hoveredCcRowSetting) {
      const ccRowSettingCopy = { ...hoveredCcRowSetting };
      ccRowSettingCopy.hovered = false;

      this.store.dispatch(CCRowActions.updateCcRowSetting({
        focusedLocationId,
        ccRowSetting: ccRowSettingCopy,
      }));
    }
  }

}
